import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiClock, FiZap, FiHeart, FiSettings } = FiIcons;

const ModuleFour = ({ data, onSave, completed }) => {
  const [formData, setFormData] = useState({
    bestWritingTimes: '',
    bestWritingLocations: '',
    idealSessionLength: '',
    whatHelpsStart: '',
    whatKeepsGoing: '',
    missedSessionPlan: '',
    restartCheckboxes: [],
    restartNotes: '',
    reframeStatement: 'Faithful progress matters more than perfect consistency.'
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(prev => ({
        ...prev,
        ...data,
        restartCheckboxes: data.restartCheckboxes || []
      }));
    }
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleRestartOption = (option) => {
    setFormData(prev => {
      const options = prev.restartCheckboxes.includes(option)
        ? prev.restartCheckboxes.filter(o => o !== option)
        : [...prev.restartCheckboxes, option];
      return { ...prev, restartCheckboxes: options };
    });
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    setTimeout(() => {
      onSave(formData, markComplete);
      setIsLoading(false);
    }, 800);
  };

  const restartOptions = [
    "Start with just 15 minutes",
    "Read the last paragraph I wrote",
    "Pray for my readers before typing",
    "Change my writing location",
    "Listen to my writing playlist",
    "Re-read my Book Clarity Statement",
    "Clear my desk or close extra tabs",
    "Shorten today’s goal and stop early",
    "Write messy on purpose — no editing allowed"
  ];

  const requiredFields = [
    'bestWritingTimes', 'bestWritingLocations', 'idealSessionLength',
    'whatHelpsStart', 'whatKeepsGoing', 'missedSessionPlan', 'reframeStatement'
  ];
  
  const canComplete = requiredFields.every(field => 
    formData[field] && formData[field].toString().trim() !== ''
  );

  return (
    <ModuleLayout
      title="Sustain Momentum"
      description="Design a writing rhythm that supports faithfulness over perfection."
      icon={FiTrendingUp}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Natural Rhythm */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <SafeIcon icon={FiClock} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Natural Rhythm</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Best Writing Times</label>
              <input
                type="text"
                value={formData.bestWritingTimes}
                onChange={(e) => handleInputChange('bestWritingTimes', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., Early morning, 9pm"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Best Locations</label>
              <input
                type="text"
                value={formData.bestWritingLocations}
                onChange={(e) => handleInputChange('bestWritingLocations', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., Home office, Cafe"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Ideal Session Length</label>
              <input
                type="text"
                value={formData.idealSessionLength}
                onChange={(e) => handleInputChange('idealSessionLength', e.target.value)}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                placeholder="e.g., 45 mins"
              />
            </div>
          </div>
        </div>

        {/* Momentum Triggers */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <SafeIcon icon={FiZap} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Momentum Triggers</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">What helps you start?</label>
              <textarea
                value={formData.whatHelpsStart}
                onChange={(e) => handleInputChange('whatHelpsStart', e.target.value)}
                rows={3}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                placeholder="Music, prayer, a clear outline..."
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">What keeps you going?</label>
              <textarea
                value={formData.whatKeepsGoing}
                onChange={(e) => handleInputChange('whatKeepsGoing', e.target.value)}
                rows={3}
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                placeholder="Progress tracking, small rewards..."
              />
            </div>
          </div>
        </div>

        {/* Grace Plan */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg">
              <SafeIcon icon={FiHeart} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Grace Plan</h3>
          </div>
          
          <div className="mb-8">
            <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">What happens when you miss a session?</label>
            <textarea
              value={formData.missedSessionPlan}
              onChange={(e) => handleInputChange('missedSessionPlan', e.target.value)}
              rows={2}
              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
              placeholder="I will acknowledge it without shame and look at tomorrow..."
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Guilt-Free Restart Plan</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {restartOptions.map(option => (
                <label key={option} className="flex items-start p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.restartCheckboxes.includes(option)}
                    onChange={() => toggleRestartOption(option)}
                    className="mt-1 w-5 h-5 rounded border-slate-200 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-4 text-slate-700 font-medium text-sm">{option}</span>
                </label>
              ))}
            </div>
            <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Additional Restart Notes</label>
            <textarea
              value={formData.restartNotes}
              onChange={(e) => handleInputChange('restartNotes', e.target.value)}
              rows={2}
              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
              placeholder="Any other specific steps to get back on track..."
            />
          </div>
        </div>

        {/* Reframe Statement */}
        <div className="bg-gradient-to-r from-[#2558ab] to-[#3a7bd5] rounded-3xl p-8 text-white shadow-xl shadow-blue-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-white/10 rounded-lg">
              <SafeIcon icon={FiSettings} />
            </div>
            <h3 className="text-xl font-bold">Personalized Reframe Statement</h3>
          </div>
          <p className="text-blue-100 text-sm mb-4">Edit this statement to be your personal mantra for the writing journey:</p>
          <textarea
            value={formData.reframeStatement}
            onChange={(e) => handleInputChange('reframeStatement', e.target.value)}
            rows={2}
            className="w-full p-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-xl font-medium italic focus:ring-2 focus:ring-white/50 focus:bg-white/20 transition-all resize-none leading-relaxed"
          />
        </div>
      </div>
    </ModuleLayout>
  );
};

export default ModuleFour;